/**
 * The code plate's fold, as one number.
 *
 * Until 2026-09-02 the fold was a class on the block and every value it moved
 * lived in `index.css`. A finger cannot drag a class: half of a pull is a
 * fraction, so the properties that depend on how far the plate has travelled
 * are computed here and arrive as inline styles. Everything that does not
 * depend on it - colours, faces, the white of the plate - stays in the
 * stylesheet, which is still where the plate is described.
 *
 * `p` is the whole state: 0 is the strip, 1 is the plate, and the way back is
 * the same interpolation read backwards. Nothing is swapped for anything else
 * at any point of it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, PointerEvent } from 'react';

import { haptic } from './telegram';

const FOLD_MS = 500;
const FOLD_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
/* A flick is an answer: half a pixel per millisecond - 500 px/s - decides the
   direction whatever the distance, and only a slow release falls back to the
   halfway rule. Both numbers are what a sheet uses on either platform. */
const FLICK = 0.5;
/** px of travel below which the gesture was a tap and not a drag. */
const TAP = 6;
/** The strip of the block's bottom padding the handle lies in. */
const HANDLE = 20;
/** The folded block's inset, on the app's own 12-14 scale for cards. */
const STRIP = 12;
/** The plate's folded side: the digits plus the line under them. */
const MINI = 50;
/** Between the folded plate and the digits - the row's own gap. */
const GAP = 14;
/** Between the expanded plate and the digits. */
const UNDER = 18;
/** The heading line inside the strip: «Код для соседа», which the plate drops. */
const LINE = 18;
/** How long a tap on the handle keeps the click that follows it quiet. */
const ECHO_MS = 500;

const HINT_SEEN = 'antifon:code-hint-seen';

/**
 * Where a release lands: the tap, the flick and the halfway rule, in that
 * order. Exported because this is the whole of the gesture's judgement and the
 * only part of it a test can hold still.
 *
 * `from` is where the drag started, `p` where it was let go, `moved` the
 * furthest it got from its start, `velocity` px per ms at the release, positive
 * downwards - which is the direction the plate grows.
 */
export function decide(from: number, p: number, moved: number, velocity: number): 0 | 1 {
  if (moved < TAP) return from > 0.5 ? 0 : 1;
  if (Math.abs(velocity) > FLICK) return velocity > 0 ? 1 : 0;
  return p > 0.5 ? 1 : 0;
}

/** What the fold has to measure rather than assume. */
export type FoldSizes = {
  /** The block's own width, padding included: the plate's side when open. */
  width: number;
  /** The natural height of the heading over the plate. */
  head: number;
  /** The natural height of everything under the digits. */
  tail: number;
};

export type FoldStyles = {
  block: CSSProperties;
  head: CSSProperties;
  slot: CSSProperties;
  plate: CSSProperties;
  hint: CSSProperties;
  row: CSSProperties;
  code: CSSProperties;
  title: CSSProperties;
  tail: CSSProperties;
};

/**
 * Every property the fold moves, as a function of `p`.
 *
 * `inset` is what the block is padded by when it is open: nothing on the
 * regent's panel, where the plate is the width of the screen's content, and 12
 * on the singer's, where the plate stands on a card of its own.
 */
export function foldStyles(
  p: number,
  sizes: FoldSizes,
  inset: number,
  transition: string,
): FoldStyles {
  const q = 1 - p;
  const side = STRIP * q + inset * p;
  const full = sizes.width - inset * 2;
  const plate = MINI + (full - MINI) * p;
  return {
    /* The two custom properties are not a flourish. An inline `background`
       would outrank `.code-block:active` for ever, and an inline `transition`
       would outrank the 90ms that press is given, so both travel as values the
       stylesheet reads and the press keeps its own rule. */
    block: {
      padding: `${side}px ${side}px ${side + HANDLE * p}px`,
      '--fold-bg': `color-mix(in srgb, var(--surface) ${q * 100}%, transparent)`,
      '--fold-motion': transition,
    } as CSSProperties,
    head: { height: `${sizes.head * p}px`, opacity: p, transition },
    slot: { height: `${(full + UNDER) * p}px`, transition },
    plate: {
      width: `${plate}px`,
      height: `${plate}px`,
      padding: `${5 + 9 * p}px`,
      borderRadius: `${10 + 12 * p}px`,
      transition,
    },
    hint: { opacity: q, transition },
    row: {
      gap: `${GAP * q}px`,
      paddingLeft: `${(MINI + GAP) * q}px`,
      minHeight: `${MINI * q}px`,
      transition,
    },
    code: { fontSize: `${30 + 10 * p}px`, transition },
    /* The seconds are not here: since 2026-09-02 they are one element in one
       place, and a thing that does not move needs nothing from `p`. */
    title: { height: `${LINE * q}px`, opacity: q, transition },
    tail: { height: `${sizes.tail * p}px`, opacity: p, transition },
  };
}

export type Fold = {
  /** Where the plate is: 0 the strip, 1 the plate, anything between it a pull. */
  p: number;
  /** Which of the two states it is in or heading for, for `aria-expanded`. */
  open: boolean;
  /** Whether the teaching sign on the thumbnail is still owed. */
  hint: boolean;
  styles: FoldStyles;
  /* Ref callbacks and not ref objects, and that is the whole of the fix of
     2026-09-03: both screens draw the block only once the server has answered,
     so an effect that measured on mount found nothing there and, having no
     dependency that ever changed, never looked again. A callback fires when the
     node arrives. */
  /** The block, whose width is the plate's when it is open. */
  block: (node: HTMLDivElement | null) => void;
  /** The heading and the tail, whose natural heights the fold interpolates. */
  head: (node: HTMLSpanElement | null) => void;
  tail: (node: HTMLSpanElement | null) => void;
  /** Everything the block itself listens to. */
  grip: {
    onPointerDown: (event: PointerEvent) => void;
    onPointerMove: (event: PointerEvent) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
    onKeyDown: (event: KeyboardEvent) => void;
  };
  /** The handle's own tap. */
  toggle: () => void;
};

/**
 * `inset` is what the block is padded by when it is open - see `foldStyles`.
 *
 * `settle` is told which state the fold came to rest in, and only when that
 * changes: the panel hangs its counter and its roster on the same answer, and a
 * drag that goes out and comes back has not changed anything. The singer's
 * plate hangs nothing on it and passes nothing.
 *
 * `prefers-reduced-motion` is not read here. The stylesheet honours it
 * wholesale with `transition-duration: 0.01ms !important`, which outranks these
 * inline transitions the way `!important` outranks anything - so the pull works
 * unchanged and nothing eases after it.
 */
export function useFold(inset: number, settle?: (open: boolean) => void): Fold {
  const [p, setP] = useState(0);
  const [dragging, setDragging] = useState(false);
  /* A placeholder for the one commit before the block's ref callback fires,
     which is never painted. It is deliberately the content width of a 375
     screen: if the measurement ever fails again, it fails visibly on every
     phone that is not that one, which is how this was found. */
  const [sizes, setSizes] = useState<FoldSizes>({ width: 339, head: 31, tail: 69 });
  const [hint, setHint] = useState(() => {
    try {
      return window.localStorage.getItem(HINT_SEEN) !== '1';
    } catch {
      // Storage refused - a browser with cookies off. The sign is worth showing
      // again more than it is worth refusing to draw.
      return true;
    }
  });

  const nodes = useRef<{
    block: HTMLDivElement | null;
    head: HTMLSpanElement | null;
    tail: HTMLSpanElement | null;
  }>({ block: null, head: null, tail: null });
  const watching = useRef<ResizeObserver | null>(null);

  /* The gesture, none of which is rendered: the travel that equals a whole
     fold, where the pull started, how far it got, and how fast it was going
     when it was let go. */
  const span = useRef(357);
  const live = useRef(0);
  const from = useRef(0);
  const moved = useRef(0);
  const startY = useRef(0);
  const lastY = useRef(0);
  const lastT = useRef(0);
  const speed = useRef(0);
  const settled = useRef(0);
  const tapped = useRef(0);
  const held = useRef(false);

  // Read on every release rather than closed over, so that a panel which
  // re-renders under the finger every two seconds cannot leave a stale one.
  const report = useRef(settle);
  report.current = settle;


  const set = useCallback((next: number) => {
    live.current = next;
    setP(next);
  }, []);

  const land = useCallback(
    (next: 0 | 1) => {
      set(next);
      if (next === 1 && hint) {
        setHint(false);
        try {
          window.localStorage.setItem(HINT_SEEN, '1');
        } catch {
          // Nothing to do: the sign comes back next time and teaches again.
        }
      }
      if (next === settled.current) return;
      settled.current = next;
      report.current?.(next === 1);
    },
    [hint, set],
  );

  const read = useCallback(() => {
    const element = nodes.current.block;
    if (element === null) return;
    const width = element.clientWidth;
    // The plate plus the 18 under it is what a whole fold is worth in pixels,
    // so a pull moves the plate by exactly as much as the finger moved.
    span.current = width - inset * 2 + UNDER;
    const next = {
      width,
      head: nodes.current.head?.offsetHeight ?? 31,
      tail: nodes.current.tail?.offsetHeight ?? 69,
    };
    // Only when something really moved. The block's padding travels with the
    // fold, so its content box - which is what a ResizeObserver watches -
    // changes on every frame of it, and a fresh object each time would be a
    // re-render per frame of an animation that needs none.
    setSizes((was) =>
      was.width === next.width && was.head === next.head && was.tail === next.tail ? was : next,
    );
  }, [inset]);

  /* Rebuilt whenever one of the three arrives or leaves, because which nodes
     exist is what changes. The heights are observed and not measured once: they
     are two blocks of text, and text that has not got its face yet is a
     different height from text that has. */
  const watch = useCallback(() => {
    watching.current?.disconnect();
    if (nodes.current.block === null) {
      watching.current = null;
      return;
    }
    const observer = new ResizeObserver(read);
    for (const node of [nodes.current.block, nodes.current.head, nodes.current.tail]) {
      if (node !== null) observer.observe(node);
    }
    watching.current = observer;
    read();
  }, [read]);

  const holdBlock = useCallback(
    (node: HTMLDivElement | null) => {
      nodes.current.block = node;
      watch();
    },
    [watch],
  );

  const holdHead = useCallback(
    (node: HTMLSpanElement | null) => {
      nodes.current.head = node;
      watch();
    },
    [watch],
  );

  const holdTail = useCallback(
    (node: HTMLSpanElement | null) => {
      nodes.current.tail = node;
      watch();
    },
    [watch],
  );

  useEffect(() => () => watching.current?.disconnect(), []);

  const onPointerDown = useCallback((event: PointerEvent) => {
    startY.current = event.clientY;
    // Reset with the gesture and not only with the component: the last position
    // of the previous pull once decided the direction of the next flick.
    lastY.current = event.clientY;
    lastT.current = event.timeStamp;
    speed.current = 0;
    from.current = live.current;
    moved.current = 0;
    held.current = true;
    setDragging(true);
    try {
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    } catch {
      // A pointer id that is not really down. The gesture still works; it just
      // ends when the finger leaves the block rather than when it lifts.
    }
  }, []);

  const onPointerMove = useCallback(
    (event: PointerEvent) => {
      if (!held.current) return;
      const dy = event.clientY - startY.current;
      const dt = event.timeStamp - lastT.current;
      if (dt > 0) speed.current = (event.clientY - lastY.current) / dt;
      lastY.current = event.clientY;
      lastT.current = event.timeStamp;
      moved.current = Math.max(moved.current, Math.abs(dy));
      set(Math.min(1, Math.max(0, from.current + dy / span.current)));
    },
    [set],
  );

  const onPointerUp = useCallback(() => {
    if (!held.current) return;
    held.current = false;
    setDragging(false);
    const pulled = moved.current >= TAP;
    if (!pulled) tapped.current = Date.now();
    /* The hand is told at the release and not at the touch, because the release
       is where the decision is: the pull answers with the plate, and what the
       motor adds is the detent the plate came to rest on. A tap gets nothing -
       a plain press is not an event, which is the rule the rest of the app
       keeps. */
    if (pulled) haptic('light');
    land(decide(from.current, live.current, moved.current, speed.current));
  }, [land]);

  const toggle = useCallback(() => {
    // A tap on the handle raises a pointerup on the block and a click on the
    // handle, and two toggles are none.
    if (Date.now() - tapped.current < ECHO_MS) return;
    land(live.current > 0.5 ? 0 : 1);
  }, [land]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      land(live.current > 0.5 ? 0 : 1);
    },
    [land],
  );

  const transition = dragging ? 'none' : `all ${FOLD_MS}ms ${FOLD_EASE}`;

  return {
    p,
    open: p > 0.5,
    hint,
    styles: foldStyles(p, sizes, inset, transition),
    block: holdBlock,
    head: holdHead,
    tail: holdTail,
    grip: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onKeyDown },
    toggle,
  };
}
